/**
 * Forum "fields" are umbrella categories (CPA, Tech, Medical, …).
 * Specific certifications are labels on a topic, not their own browse cards.
 *
 * A field is added only when:
 *   1. It is in this seed list, or
 *   2. An admin creates a category (createdByAdmin).
 *
 * Users posting a topic never create a field.
 */

export type SeededField = {
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
};

export const SEEDED_FIELDS: SeededField[] = [
  {
    slug: "accounting-finance",
    name: "Accounting & Finance",
    description: "CPA, CMA, CFA, and finance credentials",
    sortOrder: 1,
  },
  {
    slug: "bar-exam",
    name: "Bar Exam",
    description: "Legal bar examination discussion",
    sortOrder: 2,
  },
  {
    slug: "medical",
    name: "Medical Certifications",
    description: "MCAT, USMLE, NCLEX, TEAS, and allied-health exams",
    sortOrder: 3,
  },
  {
    slug: "tech",
    name: "Tech Certifications",
    description: "AWS, Microsoft, Google certs",
    sortOrder: 4,
  },
  {
    slug: "project-management",
    name: "Project Management",
    description: "PMP, CAPM, and PMI credentials",
    sortOrder: 5,
  },
  {
    slug: "general",
    name: "General Discussion",
    description: "General certification discussion",
    sortOrder: 6,
  },
];

export const SEEDED_FIELD_SLUGS = new Set(SEEDED_FIELDS.map((f) => f.slug));

export function isSeededFieldSlug(slug: string): boolean {
  return SEEDED_FIELD_SLUGS.has(slug);
}

/** Categories that appear in Browse by field. */
export function isBrowseField(cat: {
  slug: string;
  createdByAdmin?: boolean;
}): boolean {
  return isSeededFieldSlug(cat.slug) || Boolean(cat.createdByAdmin);
}

/**
 * Map a typed cert name, slug, or legacy category name onto a seeded field.
 * Unknown names land in General — they do not create a new field.
 */
export function resolveFieldSlug(raw: string): string {
  const n = raw.toLowerCase().trim().replace(/[_]+/g, "-");
  if (!n) return "general";

  if (isSeededFieldSlug(n)) return n;

  const byName = SEEDED_FIELDS.find((f) => f.name.toLowerCase() === n);
  if (byName) return byName.slug;

  const rules: Array<[RegExp, string]> = [
    [/\b(pmp|capm|pmi|prince2|scrum|csm|psm|safe)\b/, "project-management"],
    [/\b(aws|azure|gcp|google cloud|comptia|cissp|cism|cisa|ccna|ccnp|cisco|security\+|network\+|a\+|ceh|itil|linux|microsoft|power platform|pl-400|ab-400)\b/, "tech"],
    [/\b(aama-cma|cma\s*\(aama\)|certified medical assistant)\b/, "medical"],
    [/\b(cpa|cma|cfa|series\s*7|aicpa|finra)\b/, "accounting-finance"],
    [/\b(bar exam|lsat|ncbe|mpre)\b/, "bar-exam"],
    [/\b(mcat|usmle|nclex|ncsbn|teas|hesi|cna|ccma|ccrn|pance|nbcot|npte|nremt|emt|ptce|ptcb|fnp|arrt|ascp|aapc|aama|nha|cpc)\b/, "medical"],
  ];

  for (const [re, slug] of rules) {
    if (re.test(n)) return slug;
  }

  return "general";
}

export function looksLikeSeededFieldName(raw: string): boolean {
  const n = raw.toLowerCase().trim();
  return SEEDED_FIELDS.some(
    (f) => f.slug === n || f.name.toLowerCase() === n,
  );
}

const ORG_RULES: Array<[RegExp, string]> = [
  [/\b(pmi|project management institute)\b/, "project-management"],
  [/\b(comptia|amazon|aws|microsoft|google|cisco|isc2|isc\s*2|isaca|ec-council|linux foundation)\b/, "tech"],
  [/\b(aicpa|ima|cfa institute|finra)\b/, "accounting-finance"],
  [/\b(ncbe|lsac|ncbex)\b/, "bar-exam"],
  [/\b(aamc|ncsbn|nbme|fsmb|ati|elsevier|nha|aapc|aacn|nccpa|ascp|arrt|nbcot|fsbpt|nremt|ptcb|aama|aanp|ancc|credentia)\b/, "medical"],
];

function resolveOrgFieldSlug(organization: string): string | null {
  const n = organization.toLowerCase().trim();
  if (!n) return null;
  for (const [re, slug] of ORG_RULES) {
    if (re.test(n)) return slug;
  }
  const fromName = resolveFieldSlug(n);
  return fromName === "general" ? null : fromName;
}

/** Auto-suggest a field for approve. Name wins over issuing body; else General. */
export function suggestForumField(input: {
  name?: string | null;
  organization?: string | null;
}): { slug: string; source: "name" | "organization" | "fallback" } {
  const fromName = resolveFieldSlug(input.name || "");
  if (fromName !== "general") return { slug: fromName, source: "name" };
  const fromOrg = resolveOrgFieldSlug(input.organization || "");
  if (fromOrg) return { slug: fromOrg, source: "organization" };
  return { slug: "general", source: "fallback" };
}

export function fieldLabel(slug: string): string {
  return SEEDED_FIELDS.find((f) => f.slug === slug)?.name || slug;
}

/** True when a name/slug is an exam that belongs inside a field, not a field itself. */
export function isExamLikeName(raw: string): boolean {
  const n = raw.toLowerCase().trim().replace(/[_]+/g, "-");
  if (!n || isSeededFieldSlug(n) || looksLikeSeededFieldName(raw)) return false;
  const mapped = resolveFieldSlug(raw);
  return mapped !== "general" && mapped !== n;
}

/** Hub / Destinations: umbrellas only — hide leftover exam categories. */
export function isFieldDestination(cat: {
  slug: string;
  name?: string;
  createdByAdmin?: boolean;
}): boolean {
  if (!isBrowseField(cat)) return false;
  return !isExamLikeName(cat.name || cat.slug);
}

export function parseCertification(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().replace(/\s+/g, " ").slice(0, 80);
  return trimmed || null;
}

export function isGeneralDiscussionField(cat: {
  slug?: string | null;
  name?: string | null;
}): boolean {
  const slug = (cat.slug || "").toLowerCase().trim();
  const name = (cat.name || "").trim().toLowerCase();
  return slug === "general" || name === "general discussion";
}

/** Exam tag is optional for main-feed posts and for General Discussion. */
export function certificationIsRequiredForDestinations(opts: {
  postToFeed: boolean;
  postToBlog: boolean;
  studyGroupIds: string[];
  forumCategoryIds: string[];
  commentTopicIds: string[];
  forumCategories: { id: string; slug?: string | null; name?: string | null }[];
}): boolean {
  if (
    opts.postToBlog ||
    opts.studyGroupIds.length > 0 ||
    opts.commentTopicIds.length > 0
  ) {
    return true;
  }
  if (opts.forumCategoryIds.length === 0) return false;
  const byId = new Map(opts.forumCategories.map((cat) => [cat.id, cat]));
  return opts.forumCategoryIds.some((id) => {
    const cat = byId.get(id);
    return !cat || !isGeneralDiscussionField(cat);
  });
}
